import { useState, useMemo, useCallback, useRef} from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
  } from '@mui/material';
  import { useDateSort } from '../../../../hooks/useDateSort';
  import TableActionsMenu from '../../../../components/common/TableActionsMenu';
  import { AdcashGuruTargetPickMetrics } from '../../../../types/adcash.types';
  
  type Props = {
    data: AdcashGuruTargetPickMetrics;
  };
  
  export default function AdcashGuruTargetpickMetricsTable({ data }: Props) {
    const dailyStats = data.dailyStats;
    const rowsPerPage = 31;
    const [page, setPage] = useState(0);
    
    // 테이블 ref - 엑셀 다운로드용
    const tableRef = useRef<HTMLTableElement>(null);

    // 같은 날짜끼리 합산
    const aggregatedDataRaw = useMemo(() => {
      const grouped = dailyStats.reduce((acc, stat) => {
        const existing = acc.find(item => item.date === stat.date);
        if (existing) {
          existing.revenue += stat.revenue;
          existing.req += stat.req;
          existing.imp += stat.imp;
          existing.clk += stat.clk;
          existing.view += stat.view;
        } else {
          acc.push({
            date: stat.date,
            revenue: stat.revenue,
            req: stat.req,
            imp: stat.imp,
            clk: stat.clk,
            view: stat.view
          });
        }
        return acc;
      }, [] as typeof dailyStats);
      
      return grouped;
    }, [dailyStats]);

    const { sorted: aggregatedData, sortDirection, toggleSort } = useDateSort(aggregatedDataRaw);

    const paged = useMemo(
        () => aggregatedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [aggregatedData, page]
    );

    const formatCurrency = useCallback((value: number) => {
        return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(value);
    }, []);

    const calculateCTR = (imp: number, clk: number) => {
        return imp > 0 ? ((clk / imp) * 100).toFixed(2) : '0.00';
    };

    const calculateVTR = (view: number, imp: number) => {
        return imp > 0 ? ((view / imp) * 100).toFixed(2) : '0.00';
    };

    const formatNumberOnly = useCallback((value: number) => Math.round(value).toLocaleString('ko-KR'), []);

    const calculateECPM = useCallback((revenue: number, imp: number) => {
        return imp > 0 ? formatNumberOnly((revenue / imp) * 1000) : '0';
    }, [formatNumberOnly]);

    // 합계 계산
    const totals = useMemo(() => {
        const totalRevenue = aggregatedData.reduce((sum, row) => sum + row.revenue, 0);
        const totalReq = aggregatedData.reduce((sum, row) => sum + row.req, 0);
        const totalImp = aggregatedData.reduce((sum, row) => sum + row.imp, 0);
        const totalClk = aggregatedData.reduce((sum, row) => sum + row.clk, 0);
        const totalView = aggregatedData.reduce((sum, row) => sum + row.view, 0);
        const avgCTR = totalImp > 0 ? ((totalClk / totalImp) * 100).toFixed(2) : '0.00';
        const avgVTR = totalImp > 0 ? ((totalView / totalImp) * 100).toFixed(2) : '0.00';
        const avgECPM = totalImp > 0 ? formatCurrency((totalRevenue / totalImp) * 1000) : formatCurrency(0);
        
        // 전달 대비 비교
        const prevRevenue = (data.prevTotalRevenue || 0);
        const revenueDelta = totalRevenue - prevRevenue;
        const revenueDeltaPercentage = prevRevenue > 0 
            ? ((revenueDelta / prevRevenue) * 100).toFixed(0)
            : '0';
        const isPositiveRevenue = revenueDelta >= 0;

        // CTR 전달 대비 비교
        const prevCTR = data.prevTotalImp > 0 ? ((data.prevTotalClk / data.prevTotalImp) * 100).toFixed(2) : '0.00';
        const ctrDelta = (Number(avgCTR) - Number(prevCTR)).toFixed(2);
        const isPositiveCTR = Number(ctrDelta) >= 0;

        // VTR 전달 대비 비교
        const prevVTR = data.prevTotalImp > 0 ? ((data.prevTotalView / data.prevTotalImp) * 100).toFixed(2) : '0.00';
        const vtrDelta = (Number(avgVTR) - Number(prevVTR)).toFixed(2);
        const isPositiveVTR = Number(vtrDelta) >= 0;

        return {
            totalRevenue,
            totalReq,
            totalImp,
            totalClk,
            totalView,
            avgCTR,
            avgVTR,
            avgECPM,
            revenueDelta,
            revenueDeltaPercentage,
            isPositiveRevenue,
            prevCTR,
            ctrDelta,
            isPositiveCTR,
            prevVTR,
            vtrDelta,
            isPositiveVTR
        };
    }, [aggregatedData, data.prevTotalRevenue, data.prevTotalImp, data.prevTotalClk, data.prevTotalView, formatCurrency]);

    // 엑셀 다운로드용 전체 데이터 (현재 정렬 상태 그대로, 페이지네이션 무시, 합계 행 제외)
    // 퍼센트 컬럼은 소수로 전달
    const allRowsDataForExcel = useMemo(() => {
        return aggregatedData.map((row) => {
            // CTR과 VTR을 소수 형태로 (0.xx)
            const ctr = row.imp > 0 ? row.clk / row.imp : 0;
            const vtr = row.imp > 0 ? row.view / row.imp : 0;
            const ecpm = calculateECPM(row.revenue, row.imp);
            return [
                row.date,
                formatNumberOnly(row.revenue),
                row.req.toLocaleString(),
                row.imp.toLocaleString(),
                row.view.toLocaleString(),
                row.clk.toLocaleString(),
                ctr,
                vtr,
                ecpm
            ];
        });
    }, [aggregatedData, formatNumberOnly, calculateECPM]);

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <TableActionsMenu tableRef={tableRef} allRowsData={allRowsDataForExcel} />
            </Box>
            <TableContainer
                sx={{
                    overflowX: 'auto'
                }}
            >
              <Table ref={tableRef}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active
                        direction={sortDirection}
                        onClick={toggleSort}
                      >
                        날짜
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>총 매출</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Request</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Impression</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>View</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Click</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>CTR</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>VTR</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>eCPM</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                    {/* 합계 행 */}
                    <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>합계</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            <Box>
                                {formatCurrency(totals.totalRevenue)}
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: '0.75rem',
                                        color: totals.isPositiveRevenue ? '#D32F2F' : '#1976D2',
                                        ml: 1,
                                        display: 'inline-flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    ({totals.isPositiveRevenue ? '▲' : '▼'} {totals.revenueDeltaPercentage}%)
                                </Box>
                            </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.totalReq.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.totalImp.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.totalView.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.totalClk.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            <Box>
                                {totals.avgCTR}%
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: '0.75rem',
                                        color: totals.isPositiveCTR ? '#D32F2F' : '#1976D2',
                                        ml: 1,
                                        display: 'inline-flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    ({totals.isPositiveCTR ? '▲' : '▼'} {totals.ctrDelta}%)
                                </Box>
                            </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            <Box>
                                {totals.avgVTR}%
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: '0.75rem',
                                        color: totals.isPositiveVTR ? '#D32F2F' : '#1976D2',
                                        ml: 1,
                                        display: 'inline-flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    ({totals.isPositiveVTR ? '▲' : '▼'} {totals.vtrDelta}%)
                                </Box>
                            </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.avgECPM}</TableCell>
                    </TableRow>
                    {paged.map((row) => {
                        const ctr = calculateCTR(row.imp, row.clk);
                        const vtr = calculateVTR(row.view, row.imp);
                        const ecpm = calculateECPM(row.revenue, row.imp);

                        return (
                            <TableRow key={row.date}>
                                <TableCell>{row.date}</TableCell>
                                <TableCell align="right">{formatCurrency(row.revenue)}</TableCell>
                                <TableCell align="right">{row.req.toLocaleString()}</TableCell>
                                <TableCell align="right">{row.imp.toLocaleString()}</TableCell>
                                <TableCell align="right">{row.view.toLocaleString()}</TableCell>
                                <TableCell align="right">{row.clk.toLocaleString()}</TableCell>
                                <TableCell align="right">{ctr}%</TableCell>
                                <TableCell align="right">{vtr}%</TableCell>
                                <TableCell align="right">{ecpm}</TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={aggregatedData.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[rowsPerPage]}
            />
        </Box>
    );
  }
  